import { useRouter } from "expo-router";
import { Pressable, StyleSheet, View } from "react-native";

import {
  AppText,
  Badge,
  Card,
  CheckboxMark,
  EmptyState,
  ListRow,
  ProtectedScreen,
  SectionHeader,
  Stack,
} from "~/components/ui";
import { useLists } from "~/state/lists";
import { useTaskActions, useTodayTasks } from "~/state/tasks";
import { cadenceLabel } from "~/utils/cadence";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import type { Task } from "~/state/sync";

function TaskCheckRow({
  task,
  overdue,
  onToggle,
  onOpen,
}: {
  task: Task;
  overdue?: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const stylesheet = useThemedStyles(makeStyles);
  return (
    <ListRow
      title={task.title}
      description={cadenceLabel(task.cadence, task.weekday, task.month_day)}
      onPress={onOpen}
      meta={
        overdue ? (
          <Badge tone="danger">Overdue · due {task.due_on}</Badge>
        ) : (
          <Badge tone="neutral">Due today</Badge>
        )
      }
      trailing={
        <Pressable
          accessibilityRole="checkbox"
          accessibilityState={{ checked: false }}
          accessibilityLabel={`Mark ${task.title} complete`}
          onPress={onToggle}
          style={stylesheet.checkHit}
        >
          <CheckboxMark checked={false} />
        </Pressable>
      }
    />
  );
}

export default function TodayScreen() {
  const stylesheet = useThemedStyles(makeStyles);
  const router = useRouter();
  const { dueToday, overdue, completedToday } = useTodayTasks();
  const lists = useLists();
  const actions = useTaskActions();

  function listName(listId: string) {
    return lists.find((list) => list.id === listId)?.name;
  }

  return (
    <ProtectedScreen
      title="Today"
      subtitle="What is due now, and what slipped yesterday."
    >
      <Card gap="md" accent="danger">
        <SectionHeader
          title="Overdue"
          description="Still waiting from earlier days."
        />
        {overdue.length === 0 ? (
          <EmptyState
            title="Nothing overdue"
            description="Recurring tasks will land here if a day is missed."
          />
        ) : (
          <Stack gap="sm">
            {overdue.map((task) => (
              <TaskCheckRow
                key={task.id}
                task={task}
                overdue
                onToggle={() => actions.toggleComplete(task.id)}
                onOpen={() => router.push(`/tasks/${task.id}` as never)}
              />
            ))}
          </Stack>
        )}
      </Card>

      <Card gap="md" accent="mint">
        <SectionHeader
          title="Due today"
          description="Tap the box to complete. Recurring tasks roll forward."
        />
        {dueToday.length === 0 ? (
          <EmptyState
            title="A quiet day"
            description="Add a task to a list, or enjoy the blank page."
          />
        ) : (
          <Stack gap="sm">
            {dueToday.map((task) => (
              <View key={task.id} style={stylesheet.rowWrap}>
                <AppText size="xs" weight="semibold" tone="muted">
                  {listName(task.list_id)}
                </AppText>
                <TaskCheckRow
                  task={task}
                  onToggle={() => actions.toggleComplete(task.id)}
                  onOpen={() => router.push(`/tasks/${task.id}` as never)}
                />
              </View>
            ))}
          </Stack>
        )}
      </Card>

      {completedToday.length > 0 ? (
        <Card gap="md">
          <SectionHeader title="Done today" />
          <Stack gap="sm">
            {completedToday.map((task) => (
              <ListRow
                key={task.id}
                title={task.title}
                description={listName(task.list_id)}
                onPress={() => router.push(`/tasks/${task.id}` as never)}
                meta={<Badge tone="success">Completed</Badge>}
                trailing={
                  <Pressable
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: true }}
                    accessibilityLabel={`Undo ${task.title}`}
                    onPress={() => actions.toggleComplete(task.id)}
                    style={stylesheet.checkHit}
                  >
                    <CheckboxMark checked />
                  </Pressable>
                }
              />
            ))}
          </Stack>
        </Card>
      ) : null}
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    checkHit: {
      minWidth: 44,
      minHeight: 44,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: radii.m,
    },
    rowWrap: { gap: spacing.xs },
  });
